import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Room, RoomCreate, RoomType } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  name: z.string().min(1, "호실명을 입력하세요"),
  floor: z.string().optional(),
  max_guests: z.string().optional(),
  description: z.string().optional(),
  wifi_ssid: z.string().optional(),
  wifi_password: z.string().optional(),
  access_info: z.string().optional(),
  amenities: z.string().optional(),
  room_type_id: z.string().optional(),
  blackouts: z
    .array(
      z
        .object({
          start: z.string().min(1, "시작일을 입력하세요"),
          end: z.string().min(1, "종료일을 입력하세요"),
          reason: z.string().optional(),
        })
        .refine((b) => b.start < b.end, {
          message: "종료일이 시작일보다 뒤여야 합니다",
          path: ["end"],
        })
    )
    .default([]),
});

type FormValues = z.infer<typeof schema>;

interface RoomFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits an existing room; otherwise it creates one. */
  room?: Room | null;
  roomTypes: RoomType[];
  isPending?: boolean;
  /** property_id is bound by the page from the selected property. */
  onSubmit: (values: Omit<RoomCreate, "property_id">) => void;
}

export function RoomFormDialog({
  open,
  onOpenChange,
  room,
  roomTypes,
  isPending,
  onSubmit,
}: RoomFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    // ponytail: zodResolver type narrowing gap with z.default(); cast is safe — zod enforces the schema at runtime.
    // biome-ignore lint/suspicious/noExplicitAny: resolver cast
  } = useForm<FormValues>({ resolver: zodResolver(schema) as any });

  const { fields, append, remove } = useFieldArray({ control, name: "blackouts" });

  // Reset the form whenever the dialog opens (new room vs. edit).
  useEffect(() => {
    if (open) {
      reset({
        name: room?.name ?? "",
        floor: room?.floor ?? "",
        max_guests: room?.max_guests != null ? String(room.max_guests) : "",
        description: room?.description ?? "",
        wifi_ssid: room?.wifi_ssid ?? "",
        wifi_password: room?.wifi_password ?? "",
        access_info: room?.access_info ?? "",
        amenities: room?.amenities?.join(", ") ?? "",
        room_type_id: room?.room_type_id ?? "",
        blackouts: room?.blackouts ?? [],
      });
    }
  }, [open, room, reset]);

  const submit = handleSubmit((values) => {
    // Drop empty optionals so the API gets a clean payload.
    const payload: Omit<RoomCreate, "property_id"> = { name: values.name.trim() };
    if (values.floor?.trim()) payload.floor = values.floor.trim();
    if (values.max_guests?.trim()) {
      const n = Number.parseInt(values.max_guests, 10);
      if (!Number.isNaN(n)) payload.max_guests = n;
    }
    if (values.description?.trim()) payload.description = values.description.trim();
    if (values.wifi_ssid?.trim()) payload.wifi_ssid = values.wifi_ssid.trim();
    if (values.wifi_password?.trim()) payload.wifi_password = values.wifi_password.trim();
    if (values.access_info?.trim()) payload.access_info = values.access_info.trim();
    const amenities = values.amenities
      ?.split(",")
      .map((a: string) => a.trim())
      .filter(Boolean);
    if (amenities && amenities.length > 0) payload.amenities = amenities;
    if (values.room_type_id) payload.room_type_id = values.room_type_id;
    payload.blackouts = (values.blackouts ?? []).map((b) => ({
      start: b.start,
      end: b.end,
      ...(b.reason?.trim() ? { reason: b.reason.trim() } : {}),
    }));
    onSubmit(payload);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{room ? "호실 수정" : "새 호실"}</DialogTitle>
          <DialogDescription>
            호실별 정보를 등록하면 게스트 챗봇 응답에 반영됩니다.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="room-name">호실명 *</Label>
              <Input id="room-name" {...register("name")} placeholder="201호" />
              {errors.name ? (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="room-floor">위치(층/동)</Label>
              <Input id="room-floor" {...register("floor")} placeholder="2층" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="room-capacity">정원</Label>
              <Input
                id="room-capacity"
                type="number"
                min={1}
                {...register("max_guests")}
                placeholder="2"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="room-type">객실 타입</Label>
            <Controller
              control={control}
              name="room_type_id"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="room-type">
                    <SelectValue placeholder="타입 선택…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomTypes
                      .filter((rt) => rt.is_active)
                      .map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>
                          {rt.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground">타입을 지정해야 예약 재고에 잡힙니다.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="room-description">객실 설명</Label>
            <Textarea
              id="room-description"
              {...register("description")}
              placeholder="오션뷰 디럭스 룸"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="room-wifi-ssid">객실 WiFi SSID</Label>
              <Input
                id="room-wifi-ssid"
                {...register("wifi_ssid")}
                placeholder="숙소 WiFi 사용 시 비워두세요"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="room-wifi-pw">객실 WiFi 비밀번호</Label>
              <Input id="room-wifi-pw" {...register("wifi_password")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="room-access">출입 안내</Label>
            <Textarea
              id="room-access"
              {...register("access_info")}
              placeholder="도어락 비밀번호 1234*, 1층 로비에서 키 수령"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="room-amenities">편의시설 (쉼표로 구분)</Label>
            <Input
              id="room-amenities"
              {...register("amenities")}
              placeholder="발코니, 욕조, 주방"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>휴무·마감 날짜</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ start: "", end: "", reason: "" })}
              >
                <Plus className="h-4 w-4" /> 휴무 추가
              </Button>
            </div>
            {fields.map((f, i) => (
              <div key={f.id} className="flex items-end gap-2">
                <div className="space-y-1">
                  <Label htmlFor={`bo-start-${i}`} className="text-xs">
                    휴무 시작
                  </Label>
                  <Input id={`bo-start-${i}`} type="date" {...register(`blackouts.${i}.start`)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`bo-end-${i}`} className="text-xs">
                    휴무 종료(제외)
                  </Label>
                  <Input id={`bo-end-${i}`} type="date" {...register(`blackouts.${i}.end`)} />
                </div>
                <Input
                  className="flex-1"
                  placeholder="사유(선택)"
                  {...register(`blackouts.${i}.reason`)}
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {Array.isArray(errors.blackouts)
              ? errors.blackouts.map((e, i) =>
                  e?.end ? (
                    <p key={i} className="text-xs text-destructive">
                      {e.end.message}
                    </p>
                  ) : null
                )
              : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {room ? "저장" : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
